// components/account/AccountHistoricsModal/components/FiltersPanel.jsx
import { Form, Row, Col, Button,Badge } from 'react-bootstrap';
import { 
  FaSearch, 
  FaFilter, 
  FaTimes,
  FaCalendarAlt,
  FaCheck,
  FaFile
} from 'react-icons/fa';
import debounce from 'lodash/debounce';

/**
 * Panel de filtres - Gère tous les filtres de recherche
 */
const FiltersPanel = ({ 
  filters, 
  onFilterChange, 
  onResetFilters, 
  categories = [] 
}) => {
  /**
   * Gère le changement d'un filtre avec debounce pour la recherche
   */
  const handleSearchChange = debounce((value) => {
    onFilterChange('searchTerm', value);
  }, 300);

  /**
   * Gère le changement d'un filtre simple
   */
  const handleSimpleFilterChange = (filterName, value) => {
    onFilterChange(filterName, value);
  };

  /**
   * Gère le changement de date
   */
  const handleDateChange = (dateType, value) => {
    onFilterChange('dateRange', {
      ...filters.dateRange,
      [dateType]: value
    });
  };

  return (
    <div className="card mb-4 border">
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <FaFilter className="me-2" />
          Filtres
        </h6>
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={onResetFilters}
          className='filter-button'
        >
          <FaTimes className="me-1" />
          Réinitialiser
        </Button>
      </div>
      
      <div className="card-body">
        <Row className="g-3">
          {/* Recherche textuelle */}
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FaSearch className="me-1" />
                Recherche
              </Form.Label>
              <div className="input-group">
                <Form.Control
                  type="text"
                  placeholder="Nom, catégorie, description..."
                  defaultValue={filters.searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='search-input'
                />
              </div>
            </Form.Group>
          </Col>

          {/* Filtre par type de document */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Type de document</Form.Label>
              <Form.Select
                value={filters.containerFilter}
                onChange={(e) => handleSimpleFilterChange('containerFilter', e.target.value)}
                className='account-type-select'
              >
                <option value="">Tous les types</option>
                <option value="documents">Cash (Dépôts)</option>
                <option value="transaction-preuves">Transactions</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filtre par catégorie */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Catégorie</Form.Label>
              <Form.Select
                value={filters.categoryFilter}
                onChange={(e) => handleSimpleFilterChange('categoryFilter', e.target.value)}
                className='account-type-select'
              >
                <option value="">Toutes les catégories</option>
                {categories.map((cat, index) => (
                  <option key={index} value={cat.category}>
                    {cat.category} ({cat.count})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filtre par présence de document */}
          <Col md={2}>
            <Form.Group>
              <Form.Label>Document</Form.Label>
              <Form.Select
                value={filters.hasDocument === null ? '' : filters.hasDocument.toString()}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : e.target.value === 'true';
                  handleSimpleFilterChange('hasDocument', value);
                }}
                className='account-type-select'
              >
                <option value="">Tous</option>
                <option value="true">Avec document</option>
                <option value="false">Sans document</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Dates */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                <FaCalendarAlt className="me-1" />
                Période
              </Form.Label>
              <Row>
                <Col>
                  <Form.Control
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => handleDateChange('start', e.target.value)}
                    placeholder="Date de début"
                    className='search-input'
                  />
                </Col>
                <Col className="text-center align-self-center">
                  <small className="text-muted">à</small>
                </Col>
                <Col>
                  <Form.Control
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => handleDateChange('end', e.target.value)}
                    placeholder="Date de fin"
                    className='search-input'
                  />
                </Col>
              </Row>
            </Form.Group>
          </Col>

          {/* Indicateurs de filtres actifs */}
          <Col md={6}>
            <div className="h-100 d-flex align-items-end">
              <div className="d-flex flex-wrap gap-2">
                {filters.searchTerm && (
                  <Badge bg="info" className="d-flex align-items-center">
                    <FaSearch className="me-1" size={10} />
                    {filters.searchTerm}
                  </Badge>
                )}
                {filters.containerFilter && (
                  <Badge bg="primary" className="d-flex align-items-center">
                    <FaFile className="me-1" size={10} />
                    {filters.containerFilter === 'documents' ? 'Cash' : 'Transactions'}
                  </Badge>
                )}
                {filters.categoryFilter && (
                  <Badge bg="success" className="d-flex align-items-center">
                    <FaFilter className="me-1" size={10} />
                    {filters.categoryFilter}
                  </Badge>
                )}
                {filters.hasDocument !== null && (
                  <Badge bg="warning" className="d-flex align-items-center">
                    <FaCheck className="me-1" size={10} />
                    {filters.hasDocument ? 'Avec doc' : 'Sans doc'}
                  </Badge>
                )}
                {(filters.dateRange.start || filters.dateRange.end) && (
                  <Badge bg="secondary" className="d-flex align-items-center">
                    <FaCalendarAlt className="me-1" size={10} />
                    {filters.dateRange.start || 'Début'} - {filters.dateRange.end || 'Fin'}
                  </Badge>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default FiltersPanel;